import { useEffect } from "react";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered, Link as LinkIcon, Unlink,
  RemoveFormatting, Undo2, Redo2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { htmlDoEditor } from "@/lib/richText";
import Button from "./Button";
import { Select } from "./Input";
import { useFieldContext } from "./Field";
import { FOCUS_RING } from "./tokens";

/* Em nível de módulo, e não dentro do editor: um componente definido no render
   é um TIPO novo a cada renderização, e o React remontaria os doze botões a
   cada tecla digitada — perdendo foco e balão de ajuda no meio do caminho. */
function Botao({ on, label, pressed, disabled, children }) {
  return (
    <Button
      type="button" variant="ghost" size="iconSm" onClick={on} disabled={disabled}
      title={label} aria-label={label} aria-pressed={pressed}
      className={cn(pressed && "bg-slate-200 text-slate-900")}
    >
      {children}
    </Button>
  );
}

/**
 * Editor de texto com formatação — a descrição do produto.
 *
 * Substitui o textarea onde a pessoa precisava escrever a ficha de uma cadeira
 * de rodas: capacidade, medidas, o que vem na caixa. Em texto puro isso virava
 * um bloco sem negrito nem lista, e a única "formatação" possível era a quebra
 * de linha — que a loja, até pouco tempo, nem respeitava.
 *
 * A barra tem SÓ o que a loja sabe desenhar: negrito, itálico, sublinhado,
 * tachado, título e subtítulo, as duas listas e link. Não tem cor, fonte,
 * tamanho nem imagem de propósito — cada botão a mais é uma decisão de
 * design entregue a quem está cadastrando com pressa, e a página do produto
 * passa a ter uma cara por cadastro. O que o editor oferece é o mesmo que o
 * servidor aceita (HtmlSeguro.php) e que a loja renderiza (richText.js).
 *
 * Tiptap e não TinyMCE: MIT em vez de GPL (o sistema pode virar produto), e
 * o texto é desenhado na própria página em vez de num iframe — o que a
 * pessoa vê aqui é o mesmo CSS `.rich-text` que a loja usa.
 *
 * Controlado por `value`/`onChange` como um input comum, para o formulário
 * não precisar saber que existe um editor por trás.
 */
export default function RichText({ value, onChange, className, testId = "richtext" }) {
  const field = useFieldContext();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // O que a loja não desenha fica fora do editor, senão a pessoa formata
        // e a formatação some na página do produto.
        code: false,
        codeBlock: false,
        horizontalRule: false,
        link: { openOnClick: false, autolink: true, defaultProtocol: "https" },
      }),
    ],
    content: value || "",
    // Sem o parágrafo de apoio do fim e, tudo apagado, "" em vez de `<p></p>`.
    onUpdate: ({ editor: e }) => onChange(htmlDoEditor(e.getHTML())),
    editorProps: {
      attributes: {
        class: "rich-text min-h-[9rem] px-3 py-2 text-base md:text-sm leading-relaxed text-slate-900 focus:outline-none",
        "data-testid": `${testId}-content`,
        ...(field?.id ? { id: field.id } : {}),
        ...(field?.describedBy ? { "aria-describedby": field.describedBy } : {}),
      },
    },
  });

  /* O formulário pode trocar o `value` por fora — ao abrir outro produto, por
     exemplo. O editor guarda estado próprio, então precisa ser avisado; mas só
     quando o conteúdo REALMENTE mudou, senão cada tecla digitada faria o
     cursor voltar ao início (o `onUpdate` acima dispara `onChange`, que muda
     `value`, que cairia aqui de novo). */
  useEffect(() => {
    if (!editor) return;
    const atual = htmlDoEditor(editor.getHTML());
    const novo = htmlDoEditor(value);
    if (novo === atual) return;
    editor.commands.setContent(novo, { emitUpdate: false });
  }, [value, editor]);

  /* Estado dos botões. No Tiptap 3 o editor não re-renderiza a cada
     transação (seria uma renderização por tecla); o que está ativo se lê por
     um seletor, que só re-renderiza quando o resultado muda. */
  const ativo = useEditorState({
    editor,
    selector: ({ editor: e }) => e ? {
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      underline: e.isActive("underline"),
      strike: e.isActive("strike"),
      bullet: e.isActive("bulletList"),
      ordered: e.isActive("orderedList"),
      link: e.isActive("link"),
      bloco: e.isActive("heading", { level: 2 }) ? "h2" : e.isActive("heading", { level: 3 }) ? "h3" : "p",
      podeDesfazer: e.can().undo(),
      podeRefazer: e.can().redo(),
    } : null,
  });

  if (!editor || !ativo) return null;

  const cmd = () => editor.chain().focus();

  const pedirLink = () => {
    const atualHref = editor.getAttributes("link").href || "";
    // `prompt` nativo: é um campo de admin, e uma caixa própria só para uma
    // URL seria mais código para manter que o ganho em polimento.
    const url = window.prompt("Endereço do link", atualHref || "https://");
    if (url === null) return;
    if (url.trim() === "" || url.trim() === "https://") { cmd().unsetLink().run(); return; }
    cmd().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  const trocarBloco = (e) => {
    const v = e.target.value;
    if (v === "p") cmd().setParagraph().run();
    else cmd().toggleHeading({ level: v === "h2" ? 2 : 3 }).run();
  };

  return (
    <div
      data-testid={testId}
      className={cn(
        "w-full rounded-lg border bg-white transition-colors border-slate-200 hover:border-slate-300",
        // O anel de foco vai na MOLDURA, não no texto: o campo inteiro é o controle.
        "focus-within:ring-2 focus-within:ring-brand-500/50 focus-within:ring-offset-1 focus-within:ring-offset-white",
        field?.invalid && "border-red-300 hover:border-red-400",
        className,
      )}
    >
      <div
        role="toolbar" aria-label="Formatação do texto" data-testid={`${testId}-toolbar`}
        className={cn("flex flex-wrap items-center gap-0.5 px-1.5 py-1 border-b border-slate-100", FOCUS_RING)}
      >
        <Select id={`${testId}-block`} value={ativo.bloco} onChange={trocarBloco} size="sm" aria-label="Tipo de bloco"
          className="w-auto pl-2 pr-7 mr-1 text-slate-700" data-testid={`${testId}-block`}>
          <option value="p">Parágrafo</option>
          <option value="h2">Título</option>
          <option value="h3">Subtítulo</option>
        </Select>

        <Botao on={() => cmd().toggleBold().run()} label="Negrito" pressed={ativo.bold}><Bold size={15} /></Botao>
        <Botao on={() => cmd().toggleItalic().run()} label="Itálico" pressed={ativo.italic}><Italic size={15} /></Botao>
        <Botao on={() => cmd().toggleUnderline().run()} label="Sublinhado" pressed={ativo.underline}><Underline size={15} /></Botao>
        <Botao on={() => cmd().toggleStrike().run()} label="Tachado" pressed={ativo.strike}><Strikethrough size={15} /></Botao>

        <span className="w-px h-5 bg-slate-200 mx-1" aria-hidden />

        <Botao on={() => cmd().toggleBulletList().run()} label="Lista com marcadores" pressed={ativo.bullet}><List size={15} /></Botao>
        <Botao on={() => cmd().toggleOrderedList().run()} label="Lista numerada" pressed={ativo.ordered}><ListOrdered size={15} /></Botao>

        <span className="w-px h-5 bg-slate-200 mx-1" aria-hidden />

        <Botao on={pedirLink} label={ativo.link ? "Editar link" : "Inserir link"} pressed={ativo.link}><LinkIcon size={15} /></Botao>
        {ativo.link && <Botao on={() => cmd().unsetLink().run()} label="Remover link"><Unlink size={15} /></Botao>}
        <Botao on={() => cmd().unsetAllMarks().clearNodes().run()} label="Limpar formatação"><RemoveFormatting size={15} /></Botao>

        <span className="flex-1" />

        <Botao on={() => cmd().undo().run()} label="Desfazer" disabled={!ativo.podeDesfazer}><Undo2 size={15} /></Botao>
        <Botao on={() => cmd().redo().run()} label="Refazer" disabled={!ativo.podeRefazer}><Redo2 size={15} /></Botao>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}
